import { Component, computed, effect, inject, input, output } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { TNil } from '@app/app.interface';
import { ICampaignCreatorForm, ICtaForm } from '@campaign-form/campaign-form.interface';
import { IDeliverableConfigurationApi } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { IGamePlayDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/game-play/game-play.interface';
import { ExecutionListComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution-list.component';
import { ExecutionService } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { ExecutionStepStore } from './execution-step.store';

@Component({
  imports: [ExecutionListComponent, TranslateModule],
  providers: [ExecutionStepStore],
  selector: 'app-execution-step',
  templateUrl: './execution-step.component.html',
})
export class ExecutionStepComponent {
  public readonly campaignCreatorsValue = input.required<FormGroup<ICampaignCreatorForm>['value'][]>();
  public readonly selectedPlatformFeature = input.required<EPlatformFeature | null>();

  public readonly gamePlayValues = input<FormArray<FormGroup<IGamePlayDeliverableForm>>['value']>([]);
  public readonly reached = input<boolean>(false);
  public readonly ctaForm = input<FormGroup<ICtaForm> | null>(null);
  public readonly deliverableConfigurations = input<IDeliverableConfigurationApi[] | null>(null);
  public readonly customModeCreatorId = input<string | TNil>(null);

  public readonly customModeDisabled = output<void>();

  public readonly creators = computed(() => {
    return this.campaignCreatorsValue().map((cc) => cc.creator!) ?? [];
  });
  public readonly customModeCreator = computed(() => {
    return this.campaignCreatorsValue().find((cc) => cc.creatorId === this.customModeCreatorId())?.creator;
  });
  public readonly customModeCreatorName = computed(() => {
    return this.customModeCreator()?.name ?? '';
  });

  public readonly availableKpis = computed(() => this.executionStepStore.availableKpis$());

  private readonly executionStepStore = inject(ExecutionStepStore);
  private readonly executionService = inject(ExecutionService);

  private readonly addedDefaultForMode = new Set<string>();

  constructor() {
    effect(() => {
      if (this.selectedPlatformFeature()) {
        this.executionStepStore.fetchAvailableKpis(this.selectedPlatformFeature()!);
      }
    });

    effect(() => {
      const ctaForm = this.ctaForm();
      const platformFeature = this.selectedPlatformFeature();
      const creatorId = this.customModeCreatorId();
      const modeKey = creatorId ?? 'global';

      if (!ctaForm) {
        this.addedDefaultForMode.clear();

        return;
      }

      if (!this.reached() || !platformFeature) {
        return;
      }

      // When entering custom mode, always remove empty global placeholders (no executionType, no exclusiveTo).
      // Must run on every effect evaluation — not gated by addedDefaultForMode — because a new placeholder
      // could have appeared between mode switches.
      if (creatorId) {
        const { executions } = ctaForm.controls;
        const emptyGlobalIndexes = executions
          .getRawValue()
          .map((e, i) => (!e.exclusiveTo && !e.executionType ? i : -1))
          .filter((i) => i !== -1)
          .reverse();

        for (const idx of emptyGlobalIndexes) {
          executions.removeAt(idx);
        }
      }

      if (this.addedDefaultForMode.has(modeKey)) {
        return;
      }

      // Match the same visibility predicate used in the template
      const visibleCount = ctaForm.controls.executions
        .getRawValue()
        .filter((e) => (creatorId ? !e.exclusiveTo || e.exclusiveTo === creatorId : !e.exclusiveTo)).length;

      if (visibleCount === 0) {
        this.addedDefaultForMode.add(modeKey);

        ctaForm.controls.executions.push(
          this.executionService.getConfig().createForm(platformFeature, undefined, creatorId ?? undefined),
        );
      }
    });
  }
}
